import { Box, Container, Grid } from '@mui/material'
import { TitleHeader } from 'components/shop-reception/mobile/Common/TitleHeader'
import { Alert, Button, Divider, IconButton, Input } from '@fullscreendigital/carrefour-dex-fe'
import { ManageSearch as ManageSearchIcon } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import {
  getInitialValues,
  getValidationSchema,
  handleOnlineEvent,
  onSaveSealQueryError,
  onSealScanQueryError,
  onSealScanQuerySuccess
} from './helpers'
import { useNotification } from 'hooks'
import { useMutation, useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import {
  getReturoMaterialTypeOptions,
  getReturoScanTypeOptions,
  getReturoSealCounter
} from 'components/shop-reception/mobile/api'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { useFormik } from 'formik'
import { toSealPayload } from './serializers'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  SealScanForm,
  SealScanPayload,
  Notification
} from 'components/shop-reception/mobile/interfaces'
import { URLS } from 'components/shop-reception/mobile/constants'
import { getReturoId, validateScanCode } from 'components/shop-reception/mobile/helpers'
import { getShopReceptionShops } from 'components/Layout/api'
import { useOfflineElementTool } from 'components/core-ui/Offline'

const SealScan = () => {
  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const fromInventory = searchParams.get('fromInventory') === 'true'
  const sealCode = searchParams.get('sealCode')
  const materialType = searchParams.get('materialType')

  const { data: shops } = getShopReceptionShops(showNotification, true)

  const [searchCode, setSearchCode] = useState(sealCode || '')
  const [searchCodes, setSearchCodes] = useState<string[]>([])
  const [isScanned, setIsScanned] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [noInternetNotification, setNoInternetNotification] = useState<Notification | null>(null)

  const idReturo = useMemo(() => getReturoId(shops), [shops])

  const { refetch, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'RETURO_SCAN_SEALS',
    url: URLS.AUTH_RETURO_SCAN_SEALS,
    elementId: idReturo
  })

  useEffect(() => {
    const handleOnline = (e: Event) =>
      handleOnlineEvent(
        refetch,
        showNotification,
        setNotifications,
        deleteOfflineData,
        setNoInternetNotification
      )

    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [refetch])

  useEffect(() => {
    const handleOffline = () => {
      setNoInternetNotification({
        message:
          'Nu aveti conexiune la internet. Puteti insa cotinua procesul, datele sunt retinute si vor fi salvate cand veti avea o conexiune valida.',
        type: 'error'
      })
      
    }
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const resetValues = () => {
    setSearchCode('')
  }

  const { data: counter, refetch: refetchCounter } = getReturoSealCounter(showNotification)

  const { data: materialTypes } = getReturoMaterialTypeOptions(showNotification)

  const { data: scanTypes } = getReturoScanTypeOptions(showNotification)

  const sealScanQuery = useQuery(
    ['returo-seal-scan'],
    async () => {
      return await makeCoreGetRequest(`/shop-reception-mobile/returo/seals/${searchCode}`)
    },
    {
      initialData: undefined,
      enabled: false,
      keepPreviousData: true,

      onSuccess: data => {
        onSealScanQuerySuccess(
          data,
          showNotification,
          resetValues,
          setIsScanned,
          formik,
          setSearchCode
        )
      },
      onError: async (res: any) => {
        await onSealScanQueryError({
          response: res,
          searchCodes,
          searchCode,
          setSearchCodes,
          saveOfflineData,
          setIsScanned,
          showNotification
        })
      }
    }
  )

  const saveSeal = async (values: SealScanPayload) =>
    await makeCorePostRequest('/shop-reception-mobile/returo/seals', values)

  const { mutate } = useMutation(saveSeal, {
    onSuccess: () => {
      resetValues()
      refetchCounter()
      showNotification({
        message: fromInventory
          ? 'Sigiliul a fost trecut în predare cu succes.'
          : 'Sigiliul a fost scanat cu succes.',
        type: 'success'
      })
      setIsScanned(false)
      if (fromInventory) {
        navigate(URLS.AUTH_RETURO_IN_PROGRESS_SEALS)
      }
    },
    onError: async (res: any) => {
      await onSaveSealQueryError({
        response: res,
        searchCodes,
        searchCode,
        setSearchCodes,
        setSearchCode,
        saveOfflineData,
        formik,
        showNotification
      })
    }
  })

  const initialValues = useMemo(
    () => getInitialValues(fromInventory, materialType),
    [fromInventory, materialType]
  )

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: SealScanForm) => {
      if (!fromInventory && !isScanned) {
        showNotification({
          message: 'Vă rugăm scanati sigiliul mai întâi!',
          type: 'error'
        })
        return
      }
      mutate(toSealPayload(searchCode, values))
    }
  })

  const isSubmitButtonDisabled = useMemo(
    () =>
      !searchCode ||
      !formik.values.materialType?.value ||
      !formik.values.scanType?.value ||
      formik.isSubmitting,
    [searchCode, formik.values.materialType, formik.values.scanType, formik.isSubmitting]
  )

  const scan = () => {
    if (!searchCode) return

    if (!validateScanCode(showNotification, searchCode, '', 'Cod invalid pentru sigiliu.')) return

    sealScanQuery.refetch()
  }

  const closeNotification = (alertIndex: number) => {
    const index = notifications.findIndex((notification, i) => i === alertIndex)
    if (index > -1) {
      const newNotifications = [...notifications]
      newNotifications.splice(index, 1)
      setNotifications(newNotifications)
    }
  }

  const finalizeScan = () => {
    setNotifications([])
    navigate('/returo')
  }

  return (
    <Container maxWidth="xs" style={{ marginTop: '20px' }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TitleHeader title={`ID Returo: ${idReturo}`} />
        </Grid>

        {noInternetNotification && (
          <Grid item xs={12}>
            <Alert severity="error">{noInternetNotification.message}</Alert>
          </Grid>
        )}

        {notifications.map((notification, index) => (
          <Grid item xs={12} key={index}>
            <Alert
              key={index}
              severity={notification.type === 'success' ? 'success' : 'error'}
              onClose={() => closeNotification(index)}
            >
              {notification.message}
            </Alert>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Box style={{ display: 'flex' }}>
            <Input
              type="text"
              label="Cod sigiliu returo"
              variant="standard"
              autoFocus
              value={searchCode}
              onChange={event => setSearchCode((event.target as HTMLInputElement).value)}
              onKeyUp={event => {
                const target = event.target as HTMLInputElement
                if (event.which == 0) {
                  if (target.value) {
                    setSearchCode((target as HTMLInputElement).value)
                    scan()
                  }
                }
              }}
              width="100%"
              id="scan-input"
            />
            <IconButton onClick={scan}>
              <ManageSearchIcon />
            </IconButton>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid xs={12} item>
          <FormInfoItems
            items={[
              {
                label: 'Numar sigilii inventariate:',
                name: counter?.inventoried,
                itemStyle: { gridTemplateColumns: '1fr 100px' }
              },
              {
                label: 'Numar sigilii in predare scanati:',
                name: counter?.inDelivery,
                itemStyle: { gridTemplateColumns: '1fr 100px' }
              }
            ]}
          />
        </Grid>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="materialType"
            label="Selectează material"
            value={formik.values.materialType}
            options={materialTypes || []}
            onChange={value => {
              formik.setFieldValue('materialType', value)
            }}
            error={!!formik.errors.materialType}
            helperText={formik.errors.materialType as string}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Divider component="div" />
        </Grid>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            name="scanType"
            label="Selectează tip scanare"
            value={formik.values.scanType}
            options={scanTypes || []}
            onChange={value => {
              formik.setFieldValue('scanType', value)
            }}
            error={!!formik.errors.scanType}
            helperText={formik.errors.scanType as string}
            disabled={fromInventory}
            required
          />
        </Grid>
        <Grid xs={12} item>
          <Button
            type="submit"
            variant="contained"
            label="Pasul următor"
            style={{ width: '100%' }}
            onClick={() => formik.handleSubmit()}
            disabled={isSubmitButtonDisabled}
          />
        </Grid>
        {!fromInventory && (
          <Grid xs={12} item>
            <Button
              type="submit"
              variant="contained"
              label="Finalizează"
              style={{ width: '100%' }}
              onClick={finalizeScan}
              disabled={!!noInternetNotification}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  )
}

export default SealScan
